import { CONTACT_FORM_DETAILS } from '@config/contact-us-links';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormButton from '@common/button/button';
import * as Sentry from '@sentry/browser';
import { mailService } from '@services/mail/mail-service';
import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import ReactTooltip from 'react-tooltip';

interface FormData {
    name: string;
    email: string;
    message: string;
}

const ContactUsForm: React.FC = () => {
    const { register, handleSubmit, errors, reset } = useForm<FormData>();

    const recaptchaRef = useRef(null);
    const [successMessage, showSuccessMessage] = useState(false);
    const [genericError, showGenericError] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [captchaError, showCaptchaError] = useState(true);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const onSubmit = handleSubmit(async ({ name, email, message }: FormData) => {
        try {
            setFormSubmitted(true);
            showSuccessMessage(false);
            setDisabled(true);
            showGenericError(false);
            if (!captchaError) {
                const { type, to } = CONTACT_FORM_DETAILS;
                const result = await mailService.sendMail(type, to, name, email, message);

                if (result.status === 201) {
                    reset();
                    recaptchaRef.current.reset();
                    showSuccessMessage(true);
                } else {
                    showGenericError(true);
                }
            }
            setDisabled(false);
        } catch (error) {
            showGenericError(true);
            setDisabled(false);
            Sentry.captureException(error);
        }
    });

    const rebuildTooltip = () => {
        setTimeout(() => {
            ReactTooltip.rebuild();
        }, 10);
    };

    const onCaptchaChange = () => {
        showCaptchaError(false);
    };

    return (
        <form name="contact-us-form" className="inline-block w-full">
            <ReactTooltip
                id="validation"
                className="w-1/2 md:w-4/12 lg:w-2/12 xxl:w-1/12 text-xs bg-charcoal"
                effect="solid"
                event="focusin"
                eventOff="blur"
                multiline={true}
                place="left"
                delayShow={250}
            />
            <div className="mb-4 relative max-w-[280px] lg:max-w-[400px]">
                <input
                    className="base-text-size-p relative input border-b border-white pt-1 h-10 bg-realm-dark placeholder-white text-white min-w-[280px] lg:min-w-[400px]"
                    id="name"
                    name="name"
                    placeholder="Your Name"
                    onFocus={rebuildTooltip}
                    onChange={rebuildTooltip}
                    autoFocus
                    ref={register({
                        required: {
                            value: true,
                            message: 'This field is required',
                        },
                    })}
                />
                {errors.name && (
                    <span className="base-error-icon">
                        <FontAwesomeIcon
                            className="fa-lg"
                            icon={faExclamationCircle}
                            data-tip={errors.name.message}
                            data-for="validation"
                        />
                    </span>
                )}
            </div>
            <div className="mb-4 relative max-w-[280px] lg:max-w-[400px]">
                <input
                    className="base-text-size-p relative input border-b border-white pt-1 h-10 bg-realm-dark placeholder-white text-white min-w-[280px] lg:min-w-[400px]"
                    id="email"
                    name="email"
                    placeholder="Your Email"
                    onFocus={rebuildTooltip}
                    onChange={rebuildTooltip}
                    ref={register({
                        required: {
                            value: true,
                            message: 'This field is required',
                        },
                        maxLength: {
                            value: 50,
                            message: 'This field only accepts a maximum of 50 characters',
                        },
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                            message: 'This field only accepts valid email',
                        },
                    })}
                />
                {errors.email && (
                    <span className="base-error-icon">
                        <FontAwesomeIcon
                            className="fa-lg"
                            icon={faExclamationCircle}
                            data-tip={errors.email.message}
                            data-for="validation"
                        />
                    </span>
                )}
            </div>
            <div className="mb-4 relative max-w-[280px] lg:max-w-[400px]">
                <textarea
                    className="base-text-size-p relative input border-b border-white pt-1 bg-realm-dark placeholder-white text-white h-10 min-w-[280px] lg:min-w-[400px]"
                    id="message"
                    name="message"
                    placeholder="Share your thoughts"
                    onFocus={rebuildTooltip}
                    onChange={rebuildTooltip}
                    ref={register({
                        required: {
                            value: true,
                            message: 'This field is required',
                        },
                    })}
                />
                {errors.message && (
                    <span className="base-error-icon right-6">
                        <FontAwesomeIcon
                            className="fa-lg"
                            icon={faExclamationCircle}
                            data-tip={errors.message.message}
                            data-for="validation"
                        />
                    </span>
                )}
            </div>
            <div className="mb-2 text-left">
                <ReCAPTCHA
                    ref={recaptchaRef}
                    className="inline-block"
                    sitekey="6LePVqkaAAAAAF4C2MuNTg7NyNtFk7EfcUSe4BjR"
                    onChange={onCaptchaChange}
                />
            </div>
            <div className="text-left">
                <FormButton
                    onClick={onSubmit}
                    disabled={disabled}
                    label="SUBMIT"
                    extraClassName="border-white text-white hover:bg-white hover:text-tan"
                />
            </div>
            <div className="w-full base-text-size-6 text-left mt-4 text-[#ff0000]">
                {successMessage && (
                    <span className="SuccessMessage block base-text-size-6 font-medium mb-4 text-tan font-kg-medium">
                        Thank you for contacting us, you&apos;ll received a response from us in a while.
                    </span>
                )}
                {genericError && (
                    <span className="errorMessage text-red block base-text-size-6 font-medium mb-4">
                        Something went wrong, please try again
                    </span>
                )}
                {captchaError && formSubmitted && (
                    <span className="errorMessage text-red block base-text-size-6 font-medium mb-4">
                        Invalid Captcha
                    </span>
                )}
            </div>
        </form>
    );
};

export default ContactUsForm;
